import { useQuery } from "@apollo/client";
import { InfoOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  CardActionArea,
  Chip,
  CircularProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import { Title, useTranslate } from "react-admin";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { ListPagination } from "../components/ListPagination";
import { RequirePermission } from "../components/RequirePermission";
import {
  BILLWERK_CUSTOMER_MATCH_ROUTE,
  BILLWERK_PLAN_VARIANTS_ROUTE,
  FINANCE_NOTIFICATIONS_ROUTE,
  ResourceName,
} from "../constants/resources";
import {
  BillwerkListStatus,
  FinanceBillwerkCheck,
  FinanceOverviewDocument,
  FinanceOverviewList,
  type FinanceOverviewQuery,
  type FinanceOverviewQueryVariables,
  FinanceOverviewSort,
  FinanceReconciliationStatus,
  FinanceSalesforceCheck,
} from "../generated/graphql";
import { formatCrmCount, formatCrmCurrency, formatCrmDate, formatCrmDateTime, formatCrmDayOf } from "../lib/crm";
import { typography } from "../theme/designTokens";

type Overview = FinanceOverviewQuery["financeOverview"];
type ReconciliationRow = Overview["reconciliation"][number];
type CheckItem = Overview["salesforceItems"][number];
type BillwerkContract = Overview["billwerkContracts"][number];
type AccountRef = ReconciliationRow["accounts"][number];
type Variant = ReconciliationRow["variants"][number];

// The server's page size.
const PAGE_SIZE = 50;
const POLL_MS = 5000;
const SEARCH_DEBOUNCE_MS = 300;
// Accounts shown per row before "+ n weitere".
const SHOWN_ACCOUNTS = 3;

// The generated enums are sorted by name; the page shows them in this order.
const LISTS = [FinanceOverviewList.Reconciliation, FinanceOverviewList.Salesforce, FinanceOverviewList.Billwerk];
const STATUSES = [
  FinanceReconciliationStatus.SalesforceOnly,
  FinanceReconciliationStatus.BillwerkOnly,
  FinanceReconciliationStatus.VariantsDiffer,
  FinanceReconciliationStatus.NoBillwerkCustomer,
  FinanceReconciliationStatus.NoSalesforceAccount,
  FinanceReconciliationStatus.Unclear,
  FinanceReconciliationStatus.InSync,
];
const SALESFORCE_CHECKS = [
  FinanceSalesforceCheck.ActiveStoppedWithoutEnd,
  FinanceSalesforceCheck.BilledNotActive,
  FinanceSalesforceCheck.ZeroPrice,
  FinanceSalesforceCheck.Billomat,
];
const BILLWERK_CHECKS = [
  FinanceBillwerkCheck.Dunning,
  FinanceBillwerkCheck.BillingSuspended,
  FinanceBillwerkCheck.EndingSoon,
];

const oneOf =
  <T extends string>(values: T[]) =>
  (value: string | null): value is T =>
    values.includes(value as T);
const isList = oneOf(LISTS);
const isStatus = oneOf(STATUSES);
const isSalesforceCheck = oneOf(SALESFORCE_CHECKS);
const isBillwerkCheck = oneOf(BILLWERK_CHECKS);

// Billwerk dates come as ISO timestamps; only the day is shown.
const formatDay = (value: string | null | undefined): string | null =>
  value ? formatCrmDate(value.slice(0, 10)) : null;

const ExternalLink = ({ href, children }: { href: string | null | undefined; children: ReactNode }) =>
  href ? (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  ) : (
    <>{children}</>
  );

const Secondary = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" color="text.secondary">
    {children}
  </Typography>
);

const Kpi = ({ label, value, hint, testId }: { label: string; value: string; hint?: string; testId: string }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: "1 1 200px", minWidth: 200 }} data-testid={testId}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={typography.fontWeight.semibold}>
      {value}
    </Typography>
    {hint && <Secondary>{hint}</Secondary>}
  </Paper>
);

const Accounts = ({ accounts }: { accounts: AccountRef[] }) => {
  const translate = useTranslate();
  if (accounts.length === 0) return <Secondary>{translate("financeOverview.noAccounts")}</Secondary>;
  return (
    <Stack gap={0.25}>
      {accounts.slice(0, SHOWN_ACCOUNTS).map(account => (
        <ExternalLink key={account.salesforceId} href={account.url}>
          {account.name ?? account.salesforceId}
        </ExternalLink>
      ))}
      {accounts.length > SHOWN_ACCOUNTS && (
        <Secondary>{translate("financeOverview.moreAccounts", { count: accounts.length - SHOWN_ACCOUNTS })}</Secondary>
      )}
    </Stack>
  );
};

// What one plan variant's counts mean, line by line.
const VariantCounts = ({ variant }: { variant: Variant }) => {
  const translate = useTranslate();
  const { salesforceItems, billwerkQuantity } = variant;
  const differs = variant.planVariantId === null || salesforceItems !== billwerkQuantity;
  let verdict = "equal";
  if (variant.planVariantId === null) verdict = "noVariant";
  else if (billwerkQuantity === 0) verdict = "salesforceOnly";
  else if (salesforceItems === 0) verdict = "billwerkOnly";
  else if (salesforceItems > billwerkQuantity) verdict = "billwerkLess";
  else if (salesforceItems < billwerkQuantity) verdict = "billwerkMore";
  const hint = (
    <Stack gap={0.5} data-testid="finance-variant-hint">
      <span>
        {translate("financeOverview.variantHint.salesforce", {
          count: formatCrmCount(salesforceItems),
          value: formatCrmCurrency(variant.salesforceMonthlyValue),
        })}
      </span>
      <span>
        {translate("financeOverview.variantHint.billwerk", {
          quantity: formatCrmCount(billwerkQuantity),
          contracts: formatCrmCount(variant.billwerkContracts),
        })}
      </span>
      {variant.known === false && <span>{translate("financeOverview.variantHint.unknown")}</span>}
      <span>{translate(`financeOverview.variantHint.${verdict}`)}</span>
    </Stack>
  );
  return (
    <Tooltip title={hint} arrow>
      <Typography
        variant="body2"
        tabIndex={0}
        sx={{ whiteSpace: "nowrap", cursor: "help", fontWeight: differs ? typography.fontWeight.semibold : undefined }}
        color={differs ? "warning.dark" : "text.secondary"}
      >
        {translate("financeOverview.variantCounts", {
          salesforce: formatCrmCount(salesforceItems),
          billwerk: formatCrmCount(billwerkQuantity),
        })}
      </Typography>
    </Tooltip>
  );
};

const ReconciliationRowView = ({ row, showStatus }: { row: ReconciliationRow; showStatus: boolean }) => {
  const translate = useTranslate();
  const matchSearch = row.debitorAccount ?? row.customer?.id ?? row.accounts[0]?.salesforceId ?? null;
  return (
    <TableRow data-testid="finance-reconciliation-row">
      <TableCell sx={{ verticalAlign: "top" }}>
        {row.customer ? (
          <ExternalLink href={row.customer.url}>{row.customer.name ?? row.customer.id}</ExternalLink>
        ) : (
          <Secondary>{translate("financeOverview.noCustomer")}</Secondary>
        )}
        {row.debitorAccount && <Secondary>{row.debitorAccount}</Secondary>}
        {showStatus && (
          <Chip
            size="small"
            variant="outlined"
            sx={{ mt: 0.5 }}
            label={translate(`financeOverview.statuses.${row.status}`)}
          />
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <Accounts accounts={row.accounts} />
        {(row.status === FinanceReconciliationStatus.Unclear ||
          row.status === FinanceReconciliationStatus.NoBillwerkCustomer ||
          row.status === FinanceReconciliationStatus.NoSalesforceAccount) &&
          matchSearch && (
            <Link
              component={RouterLink}
              to={`${BILLWERK_CUSTOMER_MATCH_ROUTE}?q=${encodeURIComponent(matchSearch)}`}
              variant="body2"
            >
              {translate("financeOverview.customerMatchLink")}
            </Link>
          )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <Stack gap={0.5}>
          {row.variants.map(variant => (
            <Stack
              key={variant.planVariantId ?? "none"}
              direction="row"
              gap={1}
              justifyContent="space-between"
              data-testid="finance-reconciliation-variant"
            >
              <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                {variant.planVariantId ? (
                  <>
                    <ExternalLink href={variant.url}>{variant.name ?? variant.planVariantId}</ExternalLink>
                    {variant.known === false && <Secondary>{translate("financeOverview.unknownVariant")}</Secondary>}
                  </>
                ) : (
                  <Secondary>{translate("financeOverview.noVariant")}</Secondary>
                )}
              </Box>
              <VariantCounts variant={variant} />
            </Stack>
          ))}
        </Stack>
      </TableCell>
      <TableCell sx={{ verticalAlign: "top", minWidth: 150 }}>
        {row.salesforceCreatedAt && (
          <Secondary>
            {translate("financeOverview.salesforceCreatedOn", { date: formatCrmDayOf(row.salesforceCreatedAt) })}
          </Secondary>
        )}
        {row.billwerkStartedAt && (
          <Secondary>
            {translate("financeOverview.billwerkStartedOn", { date: formatCrmDayOf(row.billwerkStartedAt) })}
          </Secondary>
        )}
        {!row.salesforceCreatedAt && !row.billwerkStartedAt && "–"}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }} align="right">
        {row.salesforceItems > 0 ? formatCrmCurrency(row.monthlyValue) : "–"}
      </TableCell>
    </TableRow>
  );
};

const CheckItemRow = ({ item }: { item: CheckItem }) => {
  const translate = useTranslate();
  const start = item.startDate ? formatCrmDate(item.startDate) : null;
  const end = item.endDate ? formatCrmDate(item.endDate) : null;
  return (
    <TableRow data-testid="finance-check-item">
      <TableCell sx={{ verticalAlign: "top" }}>
        <ExternalLink href={item.salesforceUrl}>{item.name ?? item.salesforceId}</ExternalLink>
        {item.status && <Secondary>{item.status}</Secondary>}
        {item.createdAt && (
          <Secondary>{translate("financeOverview.createdOn", { date: formatCrmDayOf(item.createdAt) })}</Secondary>
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{item.accountName ?? "–"}</TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        {item.contractUrl ? (
          <ExternalLink href={item.contractUrl}>{item.contractNumber ?? item.contractUrl}</ExternalLink>
        ) : (
          "–"
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top", overflowWrap: "anywhere" }}>{item.productName ?? "–"}</TableCell>
      <TableCell sx={{ verticalAlign: "top" }} align="right">
        {item.price === null || item.price === undefined ? "–" : formatCrmCurrency(item.price)}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        {start && <Typography variant="body2">{translate("financeOverview.from", { date: start })}</Typography>}
        {end && <Typography variant="body2">{translate("financeOverview.until", { date: end })}</Typography>}
        {!start && !end && "–"}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{item.billingTool ?? "–"}</TableCell>
    </TableRow>
  );
};

const BillwerkContractRow = ({ contract, check }: { contract: BillwerkContract; check: FinanceBillwerkCheck }) => {
  const translate = useTranslate();
  const since = formatDay(contract.dunningSince);
  return (
    <TableRow data-testid="finance-billwerk-contract">
      <TableCell sx={{ verticalAlign: "top" }}>
        <ExternalLink href={contract.customer.url}>{contract.customer.name ?? contract.customer.id}</ExternalLink>
        {contract.customer.debitorAccount && <Secondary>{contract.customer.debitorAccount}</Secondary>}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <Accounts accounts={contract.accounts} />
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        {contract.planVariantName ?? contract.planVariantId ?? "–"}
        {contract.quantity !== null && contract.quantity !== undefined && contract.quantity !== 1 && (
          <Secondary>
            {translate("financeOverview.quantity", { quantity: formatCrmCount(contract.quantity) })}
          </Secondary>
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        {contract.lifecycleStatus ?? "–"}
        {contract.startDate && (
          <Secondary>{translate("financeOverview.startedOn", { date: formatDay(contract.startDate) })}</Secondary>
        )}
        {contract.billingSuspended && (
          <Chip size="small" variant="outlined" sx={{ mt: 0.5 }} label={translate("financeOverview.suspended")} />
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }} align={check === FinanceBillwerkCheck.Dunning ? "right" : "left"}>
        {check === FinanceBillwerkCheck.Dunning ? (
          <>
            <Typography>{formatCrmCurrency(contract.dunningRemaining ?? 0)}</Typography>
            {contract.dunningLevel !== null && contract.dunningLevel !== undefined && (
              <Secondary>{translate("financeOverview.dunningLevel", { level: contract.dunningLevel })}</Secondary>
            )}
            {since && <Secondary>{translate("financeOverview.dunningSince", { date: since })}</Secondary>}
          </>
        ) : (
          (formatDay(contract.endDate) ?? "–")
        )}
      </TableCell>
    </TableRow>
  );
};

const FinanceOverviewView = () => {
  const translate = useTranslate();
  const [params, setParams] = useSearchParams();
  const listParam = params.get("list");
  const list = isList(listParam) ? listParam : FinanceOverviewList.Reconciliation;
  const statusParam = params.get("status");
  const status = isStatus(statusParam) ? statusParam : null;
  const salesforceParam = params.get("salesforceCheck");
  const salesforceCheck = isSalesforceCheck(salesforceParam) ? salesforceParam : SALESFORCE_CHECKS[0];
  const billwerkParam = params.get("billwerkCheck");
  const billwerkCheck = isBillwerkCheck(billwerkParam) ? billwerkParam : BILLWERK_CHECKS[0];
  const search = params.get("q") ?? "";
  const sort =
    params.get("sort") === FinanceOverviewSort.Newest ? FinanceOverviewSort.Newest : FinanceOverviewSort.Finding;
  const recentOnly = params.get("recent") === "1";
  const page = Math.max(0, Number(params.get("page")) || 0);
  const [searchInput, setSearchInput] = useState(search);

  const update = (changes: Record<string, string | null>) =>
    setParams(
      current => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) => (value === null ? next.delete(key) : next.set(key, value)));
        return next;
      },
      { replace: true }
    );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput.trim() !== search) update({ q: searchInput.trim() || null, page: null });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // Only the typed text starts a new search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const variables: FinanceOverviewQueryVariables = {
    list,
    reconciliationStatus: status,
    salesforceCheck,
    billwerkCheck,
    search: search || null,
    offset: page * PAGE_SIZE,
    sort,
    recentOnly,
  };
  const { data, loading, error, startPolling, stopPolling } = useQuery<
    FinanceOverviewQuery,
    FinanceOverviewQueryVariables
  >(FinanceOverviewDocument, { variables, fetchPolicy: "cache-and-network" });
  const result = data?.financeOverview;
  const billwerkStatus = result?.billwerk.status;
  const pending =
    billwerkStatus === BillwerkListStatus.Loading ||
    (billwerkStatus === BillwerkListStatus.Ready && result?.billwerk.contracts === BillwerkListStatus.Loading);
  useEffect(() => {
    if (!result || loading || pending || error) return;
    const lastPage = Math.max(0, Math.ceil(result.totalCount / PAGE_SIZE) - 1);
    if (page > lastPage) update({ page: lastPage > 0 ? String(lastPage) : null });
    // Clamp only after the current page and background Billwerk reads have completed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading, pending, error, page]);

  // The Billwerk lists load in the background: ask again until they are there.
  useEffect(() => {
    if (pending) startPolling(POLL_MS);
    else stopPolling();
    return () => stopPolling();
  }, [pending, startPolling, stopPolling]);

  const title = translate("financeOverview.title");
  const perMonth = (value: number) => translate("financeOverview.perMonth", { value: formatCrmCurrency(value) });
  const reconciled = result?.reconciliationCounts.reduce((sum, count) => sum + count.count, 0) ?? 0;
  const inSync =
    result?.reconciliationCounts.find(count => count.status === FinanceReconciliationStatus.InSync)?.count ?? 0;
  const listCount = (option: FinanceOverviewList): number => {
    if (!result) return 0;
    if (option === FinanceOverviewList.Reconciliation) return reconciled - inSync;
    const counts: { count: number }[] =
      option === FinanceOverviewList.Salesforce ? result.salesforceCounts : result.billwerkCounts;
    return counts.reduce((sum, count) => sum + count.count, 0);
  };
  const unreadable = result?.salesforceSync.unreadableFields.join(", ") ?? "";
  const rows = result
    ? result.reconciliation.length + result.salesforceItems.length + result.billwerkContracts.length
    : 0;
  const selectedHint =
    list === FinanceOverviewList.Reconciliation
      ? status && translate(`financeOverview.statusHints.${status}`)
      : translate(
          `financeOverview.checkHints.${list === FinanceOverviewList.Salesforce ? salesforceCheck : billwerkCheck}`
        );

  return (
    <Stack gap={3} data-testid="finance-overview">
      <Title title={title} />
      <Box>
        <Typography variant="overline" color="text.secondary">
          {translate("financeNotifications.section")}
        </Typography>
        <Typography component="h1" variant="h5" fontWeight={typography.fontWeight.semibold}>
          {title}
        </Typography>
        <Typography color="text.secondary">{translate("financeOverview.intro")}</Typography>
        {result && (
          <Box mt={1}>
            {result.billwerk.loadedAt && result.billwerk.contractCount !== null && (
              <Typography color="text.secondary">
                {translate("financeOverview.billwerkLoadedAt", {
                  time: formatCrmDateTime(result.billwerk.loadedAt),
                  customers: formatCrmCount(result.billwerk.customerCount ?? 0),
                  contracts: formatCrmCount(result.billwerk.contractCount ?? 0),
                })}
              </Typography>
            )}
            <Typography color="text.secondary">
              {result.salesforceSync.lastSucceededAt
                ? translate("financeOverview.lastSync", {
                    time: formatCrmDateTime(result.salesforceSync.lastSucceededAt),
                  })
                : translate("financeOverview.noSync")}
            </Typography>
          </Box>
        )}
      </Box>
      {error && <Alert severity="error">{translate("financeOverview.loadError")}</Alert>}
      {loading && !data && <CircularProgress aria-label={translate("financeOverview.loading")} />}
      {pending && (
        <Alert severity="info" icon={<CircularProgress size={20} />}>
          {translate("financeOverview.billwerkLoading")}
        </Alert>
      )}
      {billwerkStatus === BillwerkListStatus.Unavailable && (
        <Alert severity="error">{translate("financeOverview.billwerkUnavailable")}</Alert>
      )}
      {billwerkStatus === BillwerkListStatus.NotConfigured && (
        <Alert severity="warning">{translate("financeOverview.billwerkNotConfigured")}</Alert>
      )}
      {result?.withheld && (
        <Alert severity="warning">
          {unreadable
            ? translate("financeOverview.withheld", { fields: unreadable })
            : translate("financeOverview.notCopied")}
        </Alert>
      )}
      {result && (
        <>
          <Stack direction="row" flexWrap="wrap" gap={2}>
            <Kpi
              testId="finance-kpi-billed"
              label={translate("financeOverview.kpis.billed")}
              value={perMonth(result.kpis.billedMonthlyValue)}
              hint={translate("financeOverview.kpis.billedHint", { count: formatCrmCount(result.kpis.billedItems) })}
            />
            <Kpi
              testId="finance-kpi-billwerk"
              label={translate("financeOverview.kpis.billwerkActive")}
              value={
                result.kpis.billwerkActiveContracts === null || result.kpis.billwerkActiveContracts === undefined
                  ? "–"
                  : formatCrmCount(result.kpis.billwerkActiveContracts)
              }
              hint={
                result.kpis.billwerkActiveCustomers === null || result.kpis.billwerkActiveCustomers === undefined
                  ? undefined
                  : translate("financeOverview.kpis.billwerkActiveHint", {
                      customers: formatCrmCount(result.kpis.billwerkActiveCustomers),
                    })
              }
            />
            <Kpi
              testId="finance-kpi-dunning"
              label={translate("financeOverview.kpis.dunning")}
              value={
                result.kpis.dunningRemaining === null || result.kpis.dunningRemaining === undefined
                  ? "–"
                  : formatCrmCurrency(result.kpis.dunningRemaining)
              }
              hint={
                result.kpis.dunningContracts === null || result.kpis.dunningContracts === undefined
                  ? undefined
                  : translate("financeOverview.kpis.dunningHint", {
                      count: formatCrmCount(result.kpis.dunningContracts),
                    })
              }
            />
            <Kpi
              testId="finance-kpi-in-sync"
              label={translate("financeOverview.kpis.inSync")}
              value={reconciled > 0 ? formatCrmCount(inSync) : "–"}
              hint={
                reconciled > 0
                  ? translate("financeOverview.kpis.inSyncHint", {
                      count: formatCrmCount(inSync),
                      total: formatCrmCount(reconciled),
                    })
                  : undefined
              }
            />
          </Stack>

          <Box>
            <Typography component="h2" variant="h6" fontWeight={typography.fontWeight.semibold}>
              {translate("financeOverview.toDo.title")}
            </Typography>
            <Typography color="text.secondary" mb={1}>
              {translate("financeOverview.toDo.hint")}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {result.toDos.map(toDo => (
                <Paper
                  key={toDo.rule}
                  variant="outlined"
                  sx={{ flex: "1 1 200px", minWidth: 200 }}
                  data-testid="finance-to-do"
                >
                  <CardActionArea
                    component={RouterLink}
                    to={`${FINANCE_NOTIFICATIONS_ROUTE}?rule=${toDo.rule}`}
                    sx={{ p: 2, height: "100%" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {translate(`financeNotifications.rules.${toDo.rule}`)}
                    </Typography>
                    <Typography variant="h5" fontWeight={typography.fontWeight.semibold}>
                      {formatCrmCount(toDo.count)}
                    </Typography>
                    {toDo.count > 0 ? (
                      <>
                        <Secondary>{perMonth(toDo.monthlyValue)}</Secondary>
                        {toDo.legacy > 0 && (
                          <Secondary>
                            {translate("financeOverview.toDo.legacy", { count: formatCrmCount(toDo.legacy) })}
                          </Secondary>
                        )}
                        {toDo.upcoming > 0 && (
                          <Secondary>
                            {translate("financeOverview.toDo.upcoming", { count: formatCrmCount(toDo.upcoming) })}
                          </Secondary>
                        )}
                      </>
                    ) : (
                      <Secondary>{translate("financeOverview.toDo.none")}</Secondary>
                    )}
                  </CardActionArea>
                </Paper>
              ))}
            </Stack>
          </Box>

          <Box>
            <Tabs
              value={list}
              onChange={(_event, next: FinanceOverviewList) =>
                update({ list: next, status: null, salesforceCheck: null, billwerkCheck: null, page: null })
              }
              aria-label={translate("financeOverview.filter")}
              variant="scrollable"
            >
              {LISTS.map(option => (
                <Tab
                  key={option}
                  value={option}
                  label={translate("financeOverview.listCount", {
                    list: translate(`financeOverview.lists.${option}`),
                    count: formatCrmCount(listCount(option)),
                  })}
                />
              ))}
            </Tabs>
          </Box>
          <Typography color="text.secondary">{translate(`financeOverview.listHints.${list}`)}</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} role="group" aria-label={translate("financeOverview.filter")}>
            {list === FinanceOverviewList.Reconciliation &&
              [null, ...STATUSES].map(option => {
                const count = result.reconciliationCounts.find(each => each.status === option);
                return (
                  <Chip
                    key={option ?? "all"}
                    label={
                      option
                        ? `${translate(`financeOverview.statuses.${option}`)} (${formatCrmCount(count?.count ?? 0)})`
                        : translate("financeOverview.all")
                    }
                    color={option === status ? "primary" : "default"}
                    variant={option === status ? "filled" : "outlined"}
                    aria-pressed={option === status}
                    onClick={() => update({ status: option, page: null })}
                  />
                );
              })}
            {list === FinanceOverviewList.Salesforce &&
              result.salesforceCounts.map(count => (
                <Chip
                  key={count.check}
                  label={`${translate(`financeOverview.checks.${count.check}`)} (${formatCrmCount(count.count)})`}
                  color={count.check === salesforceCheck ? "primary" : "default"}
                  variant={count.check === salesforceCheck ? "filled" : "outlined"}
                  aria-pressed={count.check === salesforceCheck}
                  onClick={() => update({ salesforceCheck: count.check, page: null })}
                />
              ))}
            {list === FinanceOverviewList.Billwerk &&
              result.billwerkCounts.map(count => (
                <Chip
                  key={count.check}
                  label={`${translate(`financeOverview.checks.${count.check}`)} (${formatCrmCount(count.count)})`}
                  color={count.check === billwerkCheck ? "primary" : "default"}
                  variant={count.check === billwerkCheck ? "filled" : "outlined"}
                  aria-pressed={count.check === billwerkCheck}
                  onClick={() => update({ billwerkCheck: count.check, page: null })}
                />
              ))}
          </Stack>
          {selectedHint && <Alert severity="info">{selectedHint}</Alert>}
          {list === FinanceOverviewList.Salesforce && result.planVariantGaps.count > 0 && (
            <Typography color="text.secondary">
              {translate("financeOverview.planVariantGaps", {
                count: formatCrmCount(result.planVariantGaps.count),
                value: formatCrmCurrency(result.planVariantGaps.monthlyValue),
              })}{" "}
              <Link component={RouterLink} to={`${BILLWERK_PLAN_VARIANTS_ROUTE}?view=ITEMS`}>
                {translate("financeOverview.planVariantGapsLink")}
              </Link>
            </Typography>
          )}
          <Stack direction="row" flexWrap="wrap" alignItems="flex-start" gap={2}>
            <TextField
              label={translate("financeOverview.search")}
              helperText={translate("financeOverview.searchHint")}
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              size="small"
              sx={{ width: 420, maxWidth: "100%" }}
            />
            <TextField
              select
              label={translate("financeOverview.sort")}
              value={sort}
              onChange={event =>
                update({
                  sort: event.target.value === FinanceOverviewSort.Newest ? event.target.value : null,
                  page: null,
                })
              }
              size="small"
              sx={{ minWidth: 200 }}
            >
              {[FinanceOverviewSort.Finding, FinanceOverviewSort.Newest].map(option => (
                <MenuItem key={option} value={option}>
                  {translate(`financeOverview.sorts.${option}`)}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title={translate("financeOverview.recentHint", { days: result.recentDays })} arrow describeChild>
              <Chip
                label={translate("financeOverview.recentOnly", { days: result.recentDays })}
                color={recentOnly ? "primary" : "default"}
                variant={recentOnly ? "filled" : "outlined"}
                aria-pressed={recentOnly}
                onClick={() => update({ recent: recentOnly ? null : "1", page: null })}
                sx={{ mt: 0.5 }}
              />
            </Tooltip>
          </Stack>
          <TableContainer>
            {list === FinanceOverviewList.Reconciliation && (
              <Table size="small" aria-label={translate("financeOverview.lists.RECONCILIATION")}>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate("financeOverview.columns.customer")}</TableCell>
                    <TableCell>{translate("financeOverview.columns.accounts")}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        {translate("financeOverview.columns.variants")}
                        <Tooltip title={translate("financeOverview.variantHint.column")} arrow>
                          <InfoOutlined
                            fontSize="small"
                            color="action"
                            tabIndex={0}
                            aria-label={translate("financeOverview.variantHint.column")}
                            sx={{ cursor: "help" }}
                          />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>{translate("financeOverview.columns.changed")}</TableCell>
                    <TableCell align="right">{translate("financeOverview.columns.value")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.reconciliation.map(row => (
                    <ReconciliationRowView
                      key={`${row.status}:${row.customer?.id ?? row.accounts[0]?.salesforceId ?? row.debitorAccount}`}
                      row={row}
                      showStatus={status === null}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
            {list === FinanceOverviewList.Salesforce && (
              <Table size="small" aria-label={translate("financeOverview.lists.SALESFORCE")}>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate("financeOverview.columns.item")}</TableCell>
                    <TableCell>{translate("financeOverview.columns.account")}</TableCell>
                    <TableCell>{translate("financeOverview.columns.contract")}</TableCell>
                    <TableCell>{translate("financeOverview.columns.product")}</TableCell>
                    <TableCell align="right">{translate("financeOverview.columns.price")}</TableCell>
                    <TableCell>{translate("financeOverview.columns.dates")}</TableCell>
                    <TableCell>{translate("financeOverview.columns.billingTool")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.salesforceItems.map(item => (
                    <CheckItemRow key={item.salesforceId} item={item} />
                  ))}
                </TableBody>
              </Table>
            )}
            {list === FinanceOverviewList.Billwerk && (
              <Table size="small" aria-label={translate("financeOverview.lists.BILLWERK")}>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate("financeOverview.columns.customer")}</TableCell>
                    <TableCell>{translate("financeOverview.columns.accounts")}</TableCell>
                    <TableCell>{translate("financeOverview.columns.planVariant")}</TableCell>
                    <TableCell>{translate("financeOverview.columns.status")}</TableCell>
                    <TableCell align={billwerkCheck === FinanceBillwerkCheck.Dunning ? "right" : "left"}>
                      {translate(
                        billwerkCheck === FinanceBillwerkCheck.Dunning
                          ? "financeOverview.columns.dunning"
                          : "financeOverview.columns.end"
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.billwerkContracts.map(contract => (
                    <BillwerkContractRow key={contract.id} contract={contract} check={billwerkCheck} />
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          {rows === 0 && !pending && (
            <Typography color="text.secondary">{translate("financeOverview.empty")}</Typography>
          )}
          {result.totalCount > PAGE_SIZE && (
            <ListPagination
              total={result.totalCount}
              page={page + 1}
              perPage={PAGE_SIZE}
              resource={ResourceName.FinanceOverview}
              onPageChange={next => update({ page: next > 1 ? String(next - 1) : null })}
              isLoading={loading}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export const FinanceOverviewPage = () => (
  <RequirePermission permission={ResourceName.FinanceOverview}>
    <FinanceOverviewView />
  </RequirePermission>
);
